import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import styled from 'styled-components/macro';

import { useEffect, useRef } from 'react';
import { colors, distances, grid, zIndex } from '../styles/constants';
import Icon from './Icons';

interface ModalProps {
    children: any;
    fixedWidth?: boolean;
    width?: string;
    height?: string;
    solidBackdrop?: boolean;
    overflowY?: 'scroll' | 'auto' | 'hidden' | 'visible';
    onClose?: () => void;
}

const Modal = ({
    fixedWidth,
    width,
    height,
    children,
    solidBackdrop,
    overflowY,
    onClose,
}: ModalProps) => {
    const modalElement = useRef<HTMLHeadingElement>(null);
    useEffect(() => {
        if (modalElement && modalElement.current) {
            disableBodyScroll(modalElement.current);
        }
        return () => clearAllBodyScrollLocks();
    });
    return (
        <Backdrop solidBackdrop={solidBackdrop}>
            {onClose && <BackdropTrigger onClick={onClose} />}
            <ModalWrapper
                ref={modalElement}
                width={width}
                height={height}
                fixedWidth={fixedWidth || false}
                overflowY={overflowY || 'scroll'}
            >
                {onClose && <CloseButtonGroup><IconButton onClick={onClose}><Icon icon="close_small" /></IconButton></CloseButtonGroup>}
                {children}
            </ModalWrapper>
        </Backdrop>
    );
};

interface ModalWrapperProps {
    fixedWidth: boolean;
    width?: string;
    height?: string;
    overflowY: 'scroll' | 'auto' | 'hidden' | 'visible';
}

const ModalWrapper = styled.section<ModalWrapperProps>`
    background: ${colors.background};
    color: ${colors.text};
    max-width: ${(props) => (props.fixedWidth ? grid.spans.span6 : '80vw')};
    min-width: 300px;
    border-radius: 2px;
    position: absolute;
    top: 10%;
    max-height: 80%;
    z-index: ${zIndex.top + 1};
    overflow-y: ${(props) => props.overflowY};
    padding: ${distances.normal};
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
    ${(props) => (props.width ? `width: ${props.width};` : '')}
    ${(props) => (props.height ? `height: ${props.height};` : '')}

    @media (max-width: 768px) {
        margin: 0;
        padding: ${distances.small};
        max-height: 95%;
        top: 2.5%;
        height: 95%;
    }

    // hide ugly scrollbars
    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
`;

/** Transparent backdrop that covers the entire viewport*/

interface BackdropProps {
    solidBackdrop?: boolean;
}
const Backdrop = styled.div<BackdropProps>`
    background: ${(props) => (props.solidBackdrop ? 'rgba(255,255,255,1)' : 'rgba(92, 103, 112, 0.5)')};
    position: fixed;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${zIndex.top};
`;

const BackdropTrigger = styled.div`
    position: fixed;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${zIndex.top};
`;

const CloseButtonGroup = styled.div`
    position: absolute;
    right: ${distances.normal};
    top: ${distances.normal};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: stretch;
`;

const IconButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    color: ${colors.text};
    font-size: 1.5rem;
    &:hover {
        color: ${colors.primary};
    }
`;

export default Modal;
